import { useState, useEffect } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, Download } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { orpc } from '@/shared/api/client';

interface WebhookLog {
  id: number;
  source: string;
  eventType: string;
  userCount: number | null;
  status: string;
  error: string | null;
  payload: string | null;
  receivedAt: number;
  processedAt: number | null;
}

const PAGE_SIZE = 20;

function formatRelativeTime(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(timestamp * 1000).toLocaleDateString();
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString();
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'processed':
      return <Badge className="bg-green-100 text-green-700">Processed</Badge>;
    case 'failed':
      return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
    case 'received':
    default:
      return <Badge className="bg-blue-100 text-blue-700">Received</Badge>;
  }
}

export function WebhooksTab() {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchLogs = async (pageNum = page) => {
    setLoading(true);
    setError(null);
    try {
      const result = await orpc.webhooks.list({ page: pageNum, pageSize: PAGE_SIZE });
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError(String(err));
      console.error('Failed to fetch webhook logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
  }, [page]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all webhooks in batches (D1 has row limits)
      const BATCH_SIZE = 1000;
      const allPayloads: string[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const result = await orpc.webhooks.export({ limit: BATCH_SIZE, offset });

        // Extract payloads from this batch
        for (const w of result.webhooks) {
          if (w.payload) {
            allPayloads.push(w.payload);
          }
        }

        hasMore = result.hasMore;
        offset += BATCH_SIZE;
      }

      // Convert to NDJSON (newline-delimited JSON)
      const ndjson = allPayloads.join('\n');

      // Create and download file
      const blob = new Blob([ndjson], { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `webhooks-${Date.now()}.ndjson`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export webhooks:', err);
      setError(`Export failed: ${String(err)}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-red-500">{error}</p>
        <Button onClick={() => fetchLogs()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#807f7e]">
          {total} webhook{total !== 1 ? 's' : ''} received
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export All Webhooks'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchLogs(page)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Users</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Processed</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length > 0 ? (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-mono text-sm">#{log.id}</TableCell>
                  <TableCell>{log.source}</TableCell>
                  <TableCell className="font-mono text-sm">{log.eventType}</TableCell>
                  <TableCell>{log.userCount?.toLocaleString() ?? '—'}</TableCell>
                  <TableCell>{getStatusBadge(log.status)}</TableCell>
                  <TableCell className="text-sm text-[#807f7e]">
                    {formatRelativeTime(log.receivedAt)}
                  </TableCell>
                  <TableCell className="text-sm text-[#807f7e]">
                    {log.processedAt ? formatRelativeTime(log.processedAt) : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedLog(log)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No webhooks received yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#807f7e]">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} webhooks
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#807f7e] min-w-[100px] text-center">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Webhook Detail Modal */}
      <Dialog open={selectedLog !== null} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Webhook #{selectedLog?.id}</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#807f7e]">Source</p>
                  <p className="font-medium">{selectedLog.source}</p>
                </div>
                <div>
                  <p className="text-[#807f7e]">Event Type</p>
                  <p className="font-mono">{selectedLog.eventType}</p>
                </div>
                <div>
                  <p className="text-[#807f7e]">User Count</p>
                  <p className="font-medium">{selectedLog.userCount?.toLocaleString() ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[#807f7e]">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                </div>
                <div>
                  <p className="text-[#807f7e]">Received At</p>
                  <p className="font-medium">{formatTimestamp(selectedLog.receivedAt)}</p>
                </div>
                <div>
                  <p className="text-[#807f7e]">Processed At</p>
                  <p className="font-medium">
                    {selectedLog.processedAt ? formatTimestamp(selectedLog.processedAt) : '—'}
                  </p>
                </div>
              </div>

              {/* Error */}
              {selectedLog.error && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-2">Error</p>
                  <pre className="text-xs bg-red-50 text-red-700 p-3 rounded border overflow-x-auto">
                    {selectedLog.error}
                  </pre>
                </div>
              )}

              {/* Payload */}
              <div>
                <p className="text-sm font-medium text-[#807f7e] mb-2">Payload</p>
                {selectedLog.payload ? (
                  <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto max-h-64 overflow-y-auto">
                    {JSON.stringify(JSON.parse(selectedLog.payload), null, 2)}
                  </pre>
                ) : (
                  <p className="text-sm text-[#807f7e] italic">
                    No payload stored (webhooks received before payload logging was enabled)
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
